import { supabase } from './supabase';
import { errorDeLaFuncion, sePuedeReintentar } from './intake-reintento';
import { leerOrigen } from './anuncio-origen';

// Cliente del endpoint server-side `public-lead-intake` (Edge Function).
// Reemplaza el acceso directo del sitio a `customers`/`vehicles`/`leads`, que la
// RLS del backend (fix C1 del pentest) cierra para el anon key. Toda la lógica de
// dedupe/upsert del cliente + el lead (con el auto que ofrece el cliente) vive
// server-side.

export interface LeadIntakePayload {
  clientId: number | string;
  /** Email del cliente (opcional). Si viene, se hace dedupe por (email, client_id). */
  email?: string;
  /** Datos del cliente; si se omiten, no se crea/actualiza cliente. */
  customer?: {
    first_name?: string;
    last_name?: string;
    phone?: string;
    rut?: string;
    birth_date?: string;
    address?: string;
  };
  /**
   * El auto que ofrece el cliente (Compra Directa / Consignación). Con `lead` en
   * la misma llamada la edge lo guarda DENTRO del lead y no en `vehicles`
   * (ticket #96); ver auto-cliente-intake.ts. Solo, sin lead, es la llamada
   * heredada: la edge lo crea como borrador en `vehicles`.
   */
  vehicle?: Record<string, unknown>;
  /**
   * Lead a crear (type, notes, brand_id, model_id, search_params). status=pending.
   * Todo lead cuelga de un cliente: con un email nuevo sin más datos la edge crea
   * el prospecto con ese email; sin email ni datos responde 400 'lead requires
   * customer' sin escribir nada.
   */
  lead?: Record<string, unknown>;
  /**
   * OBSOLETO: era el link heredado del buscador IA (AISearchBar, ya borrado).
   * La edge nueva lo ignora y responde lead_linked=false.
   */
  leadId?: number | string | null;
}

export interface LeadIntakeResult {
  customer_id: number | null;
  vehicle_id: number | null;
  lead_id: number | null;
  needs_details: boolean;
  lead_linked?: boolean;
  error?: string;
}

const FN_URL =
  'https://miuiujntdjrjhhcysiba.supabase.co/functions/v1/public-lead-intake';

export async function submitLeadIntake(
  payload: LeadIntakePayload
): Promise<LeadIntakeResult> {
  // De qué anuncio vino, si vino de uno.
  //
  // Va acá y no en cada formulario a propósito: hay siete formularios y el
  // que se agregue mañana también tiene que atribuir. Pedirle a cada uno que
  // se acuerde es garantizar que uno se olvide, y el síntoma sería que esa
  // campaña "no trae leads".
  //
  // Entra DENTRO de `search_params`, que ya es jsonb y ya viaja: no hace falta
  // ninguna columna nueva ni cambiar el contrato de la edge function.
  const origen = typeof window === 'undefined' ? null : leerOrigen();
  const lead = origen
    ? {
        ...(payload.lead ?? {}),
        search_params: {
          ...((payload.lead?.search_params as Record<string, unknown> | undefined) ?? {}),
          anuncio: origen,
        },
      }
    : payload.lead;

  const body = {
    client_id: Number(payload.clientId),
    email: payload.email,
    customer: payload.customer,
    vehicle: payload.vehicle,
    lead,
    lead_id: payload.leadId != null ? Number(payload.leadId) : undefined,
  };

  // Preferimos el SDK (adjunta apikey/anon automáticamente); fallback a fetch.
  try {
    const { data, error } = await supabase.functions.invoke(
      'public-lead-intake',
      { body }
    );
    if (error) throw error;
    return data as LeadIntakeResult;
  } catch (err) {
    if (!sePuedeReintentar(err)) {
      throw new Error(await errorDeLaFuncion(err));
    }
    const res = await fetch(FN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${process.env.NEXT_PUBLIC_SUPABASE_KEY}`,
        apikey: process.env.NEXT_PUBLIC_SUPABASE_KEY as string,
      },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data?.error || 'Error en public-lead-intake');
    }
    return data as LeadIntakeResult;
  }
}
